import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useMatch } from '../stores/match.js';
import { useAuth } from '../stores/auth.js';
import { useToast } from '../stores/toast.js';
import { Deck } from '../components/Deck.js';
import { HandOpponent, HandOwn } from '../components/Hand.js';
import { PlayerBadge } from '../components/PlayerBadge.js';
import { Table } from '../components/Table.js';
import { CardFace } from '../components/Card.js';
import { ChipIcon } from "../components/ChipIcon.js";
import { TurnTimer } from '../components/TurnTimer.js';
import { HandSortButton } from '../components/HandSortButton.js';
import { ReactionPicker } from '../components/ReactionPicker.js';
import { ReactionBubble } from '../components/ReactionBubble.js';
import { useReactions } from '../stores/reactions.js';
import type { HandSortMode } from '../lib/handSort.js';
import { nextSortMode, sortHand } from '../lib/handSort.js';
import { haptic, hapticError, hapticSuccess } from '../lib/telegram.js';
import { sfx } from '../lib/sound.js';
import { emitAction } from '../lib/gameSocket.js';
import { buttonIntent, intentForCardTap } from '../lib/intent.js';

export function Match() {
  const state = useMatch((s) => s.state);
  const ended = useMatch((s) => s.ended);
  const turnDeadlineAt = useMatch((s) => s.turnDeadlineAt);
  const endReason = useMatch((s) => s.endReason);
  const forfeitedBy = useMatch((s) => s.forfeitedBy);
  const me = useAuth((s) => s.user);
  const pushToast = useToast((s) => s.push);
  const [selected, setSelected] = useState<number | null>(null);
  const [sortMode, setSortMode] = useState<HandSortMode>(() => {
    if (typeof window === 'undefined') return 'auto';
    const stored = window.localStorage.getItem('durakapp:hand-sort');
    return stored === 'suit' || stored === 'rank' ? stored : 'auto';
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('durakapp:hand-sort', sortMode);
  }, [sortMode]);

  const myHand = useMemo(() => {
    if (!state || !me) return [];
    const slot = state.hands.find((h) => h.playerId === me.id);
    if (!slot?.cards) return [];
    return sortHand(slot.cards, state.trumpSuit, sortMode);
  }, [state, me, sortMode]);

  // Selected-index points into the sorted myHand array. When sort changes or new cards
  // arrive, the previously highlighted card may now sit at a different index — clear
  // the selection rather than highlighting the wrong card.
  useEffect(() => {
    setSelected(null);
  }, [sortMode, state?.turnNumber]);

  // Drop any reactions left over from a previous match so they don't haunt the next one.
  useEffect(() => {
    return () => useReactions.getState().reset();
  }, [state?.matchId]);

  if (!state || !me) {
    return <div className="grid min-h-full place-items-center text-ivory/60">Загружаем партию…</div>;
  }

  const opponents = state.hands.filter((h) => h.playerId !== me.id);
  const isMyTurn = state.attackerId === me.id || state.defenderId === me.id;
  const { canTake, canPass } = buttonIntent(state, me.id);

  const onTapCard = async (i: number): Promise<void> => {
    const card = myHand[i];
    if (!card) return;
    haptic('light');
    setSelected(i);
    const intent = intentForCardTap(state, me.id, card);
    if (intent.kind === 'wait' || intent.kind === 'illegal' || !intent.action) {
      hapticError();
      pushToast(intent.reason ?? 'нельзя сейчас', 'error');
      return;
    }
    try {
      await emitAction({ matchId: state.matchId, action: intent.action });
      sfx.cardPlay();
      setSelected(null);
    } catch (err) {
      hapticError();
      pushToast(err instanceof Error ? err.message : 'отказано', 'error');
    }
  };

  const onTake = async (): Promise<void> => {
    haptic('medium');
    try {
      await emitAction({ matchId: state.matchId, action: { type: 'take', playerId: me.id } });
      sfx.cardTake();
    } catch (err) {
      hapticError();
      pushToast(err instanceof Error ? err.message : 'не удалось', 'error');
    }
  };

  const onPass = async (): Promise<void> => {
    haptic('medium');
    try {
      await emitAction({ matchId: state.matchId, action: { type: 'pass', playerId: me.id } });
      sfx.cardPass();
    } catch (err) {
      hapticError();
      pushToast(err instanceof Error ? err.message : 'не удалось', 'error');
    }
  };

  return (
    <div className="relative mx-auto flex min-h-full w-full max-w-[480px] flex-col gap-2 px-3 pb-4 pt-3">
      <div className="flex flex-col items-center gap-2">
        {opponents.map((opp) => {
          const isActive = state.attackerId === opp.playerId || state.defenderId === opp.playerId;
          return (
            <div key={opp.playerId} className="flex w-full flex-col items-center">
              <div className="relative flex items-center gap-2">
                <PlayerBadge
                  name={opp.playerId.slice(0, 8)}
                  cardCount={opp.count}
                  active={isActive}
                />
                {isActive && <TurnTimer deadlineAt={turnDeadlineAt} isMine={false} />}
                <ReactionBubble userId={opp.playerId} />
              </div>
              <HandOpponent count={opp.count} />
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-3">
        <div className="flex-1">
          <Table slots={state.table} />
        </div>
        <Deck count={state.deckCount} trump={state.trumpCard} />
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 py-2">
        <div className="flex justify-end">
          <motion.button
            disabled={!canTake}
            whileTap={{ scale: 0.96 }}
            onClick={onTake}
            className="btn-primary disabled:opacity-30 disabled:saturate-50"
          >
            Взять
          </motion.button>
        </div>
        <HandSortButton mode={sortMode} onCycle={() => { sfx.sort(); setSortMode(nextSortMode(sortMode)); }} />
        <div className="flex justify-start">
          <motion.button
            disabled={!canPass}
            whileTap={{ scale: 0.96 }}
            onClick={onPass}
            className="btn-ghost disabled:opacity-30 disabled:saturate-50"
          >
            Бито
          </motion.button>
        </div>
      </div>

      <div className="mt-auto">
        <HandOwn cards={myHand} selectedIndex={selected} onSelect={onTapCard} />
      </div>

      <div className="flex items-center justify-between pt-2">
        <div className="relative flex items-center gap-2">
          <PlayerBadge name={me.firstName} photoUrl={me.photoUrl} active={isMyTurn} />
          {isMyTurn && <TurnTimer deadlineAt={turnDeadlineAt} isMine />}
          <ReactionBubble userId={me.id} />
        </div>
        <div className="flex items-center gap-2">
          <ReactionPicker matchId={state.matchId} />
          <div className="pill-glass text-sm">
            <span>{me.starsBalance.toLocaleString('ru-RU')}</span>
            <ChipIcon size={14} />
          </div>
        </div>
      </div>

      <AnimatePresence>
        {ended && (
          <ResultOverlay
            state={state}
            myId={me.id}
            endReason={endReason}
            forfeitedBy={forfeitedBy}
            onLobby={() => {
              hapticSuccess();
              useMatch.getState().reset();
              navigate('/');
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

interface ResultOverlayProps {
  state: NonNullable<ReturnType<typeof useMatch.getState>['state']>;
  myId: string;
  endReason: 'normal' | 'timeout' | 'disconnect' | null;
  forfeitedBy: string | null;
  onLobby: () => void;
}

function ResultOverlay({ state, myId, endReason, forfeitedBy, onLobby }: ResultOverlayProps) {
  const isLoser = state.loserId === myId;
  const isDraw = state.isDraw;
  const isWinner = !isDraw && !isLoser && state.loserId !== null;

  const headline = isDraw ? 'Ничья' : isWinner ? 'Победа' : 'Поражение';
  const accent = isWinner ? 'var(--color-gold)' : isDraw ? 'var(--color-gold)' : 'var(--color-ivory)';

  // Build a sub-line that explains a non-natural ending.
  const iWasForfeited = forfeitedBy === myId;
  let subline: string | null = null;
  if (endReason === 'timeout') {
    subline = iWasForfeited ? 'Ход истёк — техническое поражение' : 'Соперник не успел сходить';
  } else if (endReason === 'disconnect') {
    subline = iWasForfeited ? 'Связь потеряна — техническое поражение' : 'Соперник отключился';
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.32 }}
      className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-6 px-8"
      style={{
        background:
          'radial-gradient(ellipse at center, color-mix(in oklab, var(--color-felt-deep) 92%, transparent) 0%, color-mix(in oklab, var(--color-felt-deep) 96%, transparent) 70%)',
      }}
    >
      <motion.div
        initial={{ scale: 0.7, rotate: -8, opacity: 0 }}
        animate={{ scale: 1, rotate: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
      >
        <CardFace card={state.trumpCard} size="lg" />
      </motion.div>
      <motion.h2
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.18, duration: 0.32 }}
        className="heading-display text-5xl"
        style={{ color: accent }}
      >
        {headline}
      </motion.h2>
      {subline && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.85 }}
          transition={{ delay: 0.32, duration: 0.32 }}
          className="-mt-3 text-center text-sm text-ivory/85"
        >
          {subline}
        </motion.p>
      )}
      <button onClick={onLobby} className="btn-primary">
        В лобби
      </button>
    </motion.div>
  );
}
